import { AutoTagRule, ExpressionMatcher } from "./api";

type ConditionToken = {
    value: string;
    quoted: boolean;
};

const headerPattern = /^tag\s+([^\s]+)\s+"((?:\\.|[^"])*)"$/i;
const groupPattern = /^(when|or)\s+(.+)$/i;
const amountPattern = /^amount\s+(>=|<=|==)\s+(-?\d+(?:\.\d+)?)$/i;
const textPattern = /^(description|tag)\s+~\s+(.+)$/i;

export class AutoTagRuleTextError extends Error {
    constructor(message: string, public readonly line: number) {
        super(`Line ${line}: ${message}`);
    }
}

export const formatAutoTagRule = (rule: AutoTagRule): string => {
    const lines = [
        `tag ${rule.tag || "new-tag"} "${escapeQuoted(rule.description || "")}"`
    ];

    rule.matchers.forEach((matcher, index) => {
        const prefix = index === 0 ? "when" : "or";
        lines.push(`${prefix} ${matcher.map(formatMatcher).join(" and ")}`);
    });

    return lines.join("\n");
};

export const parseAutoTagRule = (text: string): AutoTagRule => {
    const lines = text
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(line => line.length > 0 && !line.startsWith("#"));

    if (lines.length === 0) {
        throw new AutoTagRuleTextError("Expected tag declaration", 1);
    }

    const header = headerPattern.exec(lines[0]);
    if (!header) {
        throw new AutoTagRuleTextError('Expected: tag <name> "<description>"', 1);
    }

    const rule: AutoTagRule = {
        tag: header[1],
        description: unescapeQuoted(header[2]),
        matchers: []
    };

    for (let index = 1; index < lines.length; index++) {
        const line = lines[index];
        const group = groupPattern.exec(line);
        if (!group) {
            throw new AutoTagRuleTextError("Expected a when/or matcher group", index + 1);
        }
        rule.matchers.push(parseMatcherGroup(group[2], index + 1));
    }

    return rule;
};

const parseMatcherGroup = (raw: string, line: number): ExpressionMatcher[] => {
    const parts = splitByAnd(raw);
    if (parts.length === 0) {
        throw new AutoTagRuleTextError("Expected at least one condition", line);
    }
    return parts.map(part => parseMatcher(part, line));
};

const parseMatcher = (raw: string, line: number): ExpressionMatcher => {
    const amount = amountPattern.exec(raw);
    if (amount) {
        return {
            type: "Amount",
            expression: `${amount[1]} ${amount[2]}`
        };
    }

    const text = textPattern.exec(raw);
    if (!text) {
        throw new AutoTagRuleTextError("Invalid condition", line);
    }

    const token = parseConditionValue(text[2], line);
    return {
        type: text[1].toLowerCase() === "tag" ? "Tag" : "Description",
        expression: token.value
    };
};

const splitByAnd = (raw: string): string[] => {
    const parts: string[] = [];
    let current = "";
    let quoted = false;
    let escaped = false;

    for (let index = 0; index < raw.length; index++) {
        const char = raw[index];
        if (escaped) {
            current += char;
            escaped = false;
            continue;
        }
        if (char === "\\") {
            current += char;
            escaped = true;
            continue;
        }
        if (char === '"') {
            quoted = !quoted;
            current += char;
            continue;
        }
        if (!quoted && raw.slice(index, index + 5).toLowerCase() === " and ") {
            parts.push(current.trim());
            current = "";
            index += 4;
            continue;
        }
        current += char;
    }

    if (current.trim().length > 0) {
        parts.push(current.trim());
    }

    return parts;
};

const parseConditionValue = (raw: string, line: number): ConditionToken => {
    const value = raw.trim();
    if (!value.startsWith('"')) {
        return { value, quoted: false };
    }
    if (!value.endsWith('"') || value.length === 1) {
        throw new AutoTagRuleTextError("Unterminated quoted value", line);
    }
    return {
        value: unescapeQuoted(value.slice(1, -1)),
        quoted: true
    };
};

const formatMatcher = (matcher: ExpressionMatcher): string => {
    if (matcher.type === "Amount") {
        return `amount ${matcher.expression}`;
    }
    return `${matcher.type.toLowerCase()} ~ "${escapeQuoted(matcher.expression)}"`;
};

const escapeQuoted = (value: string): string => {
    return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
};

const unescapeQuoted = (value: string): string => {
    return value.replace(/\\"/g, '"').replace(/\\\\/g, "\\");
};

